 
import axios from "axios"
import { Environment, Network, QueryResponseCache, RecordSource, Store } from "relay-runtime"

import ajaxPath from "./api/ajaxPath"
import { post } from "./api/axios"

export const ONE_SECOND = 1_000
export const ONE_MINUTE = 60 * ONE_SECOND
export const cacheTTL = ONE_MINUTE

const MOCKED_RELAY_EMPTY_RESPONSE = { data: null }

export const SUSPENDED_ORG_ERROR_MESSAGE = "Organization access is suspended"

// Custom error class for suspended organizations
export class OrganizationSuspendedError extends Error {
  brandTheme: string | null;

  constructor(brandTheme?: string | null) {
    super(SUSPENDED_ORG_ERROR_MESSAGE);
    Object.setPrototypeOf(this, OrganizationSuspendedError.prototype);
    this.name = "OrganizationSuspendedError";
    this.brandTheme = brandTheme ?? null;
  }
}

const cache = new QueryResponseCache({ size: 250, ttl: cacheTTL })

type CacheConfig = any & {
  force: boolean
  metadata: {
    // By default, mutations invalidate the cache. If you do not want that to happen
    // you can set this.
    disableMutationInvalidation: boolean

    // axios stuff
    axios: {
      signal: AbortSignal
    }
  }
}

export const clearCache = (): void => {
  cache.clear()
}

 
function fetchQuery(operation: any, variables: any, cacheConfig: CacheConfig, uploadables: any, headers: Record<string, string>) {
  const queryID = operation.text
  const isMutation = operation.operationKind === "mutation"
  const isQuery = operation.operationKind === "query"
  const forceFetch = cacheConfig && cacheConfig.force
  const disableMutationInvalidation =
    cacheConfig && cacheConfig.metadata && cacheConfig.metadata.disableMutationInvalidation

  // Try to get data from cache on queries
  const fromCache = cache.get(queryID, variables)

  if (isQuery && fromCache !== null && !forceFetch) {
    return fromCache
  }
  if (!headers["Authorization"]) {
    throw new Error("No access token")
  }

  const orgId = localStorage.getItem("tz_current_org_id") || "default";
  
  const requestHeaders: Record<string, string> = {
    "Content-Type": "application/json",
    "Authorization": headers["Authorization"],
    // eslint-disable-next-line custom/no-hardcoded-brand-name -- HTTP header
    "X-TierZero-Org-Id": orgId,
  }

  let requestData: any = {
    query: operation.text,
    variables,
    operationName: operation.name,
  }

  // Taken from: https://github.com/facebook/relay/issues/1844#issuecomment-316893590
  if (uploadables) {
    if (!window.FormData) {
      throw new Error("Uploading files without `FormData` not supported.")
    }

    const formData = new FormData()

    // For Gqlgen, we need "operations" and "map" on form data so that it auto parses.
    // They implement this spec: https://github.com/jaydenseric/graphql-multipart-request-spec
    // "operations": A JSON encoded operations object with files replaced with null.
    //               (NOT SURE IF WE NEED TO DO THE NULL PART)
    // "map": A JSON encoded map of where files occurred in the operations.
    //        For each file, the key is the file multipart form field name and the value is an
    //        array of operations paths.
    // ORDER MATTERS!
    // So we must:
    // 1. Append "operations" first.
    // 2. Append "map" second.
    // 3. Append the files last.
    const gqlGenOperations = {
      query: operation.text,
      variables,
      operationName: operation.name,
    }

    const mapGqlGen: Record<string, string[]> = {}

    // ===== Append "operations" first. =====
    formData.append("operations", JSON.stringify(gqlGenOperations))

    // ===== Append "map" second. =====
    Object.keys(uploadables).forEach((key) => {
      mapGqlGen[key] = [`variables.${key}`]
    })

    formData.append("map", JSON.stringify(mapGqlGen))

    // ===== 3. Append the files last. =====
    Object.keys(uploadables).forEach((key) => {
      if (Object.prototype.hasOwnProperty.call(uploadables, key)) {
        formData.append(key, uploadables[key])
      }
    })

    requestData = formData
  } else {
    requestData = {
      query: operation.text,
      variables,
      operationName: operation.name,
    }
  }

  const axiosConfigs = cacheConfig?.metadata?.axios || {}

  // Otherwise, fetch data from server
  return post(ajaxPath.get("graphql"), requestData, requestHeaders, axiosConfigs)
    .then((response) => {
      // Update cache on queries
      if (isQuery && response) {
        cache.set(queryID, variables, response)
      }

      // Clear cache on mutations
      if (!disableMutationInvalidation && isMutation) {
        cache.clear()
      }

      // Sentry: If no response from gql, return an empty null payload
      // Look at the way relay handles errors here as to why we kept getting this
      // "TypeError: undefined is not an object (evaluating 'e.data')" error
      // Relay expects a "data" field to exist on the response, otherwise its sadface.
      // https://github.com/facebook/relay/blob/6d013387f27bfd5cde684033c5e1bf93afeea9d2/packages/relay-runtime/store/OperationExecutor.js#L405
      if (!response) {
        // Log an error for the operation name so we can see this in sentry.
        // logException(
        //   "relay fetchQuery",
        //   "fetchQuery",
        //   "relay-env",
        //   new Error(`Error: Empty response from GQL for query: ${operation.name}`),
        // );
        return MOCKED_RELAY_EMPTY_RESPONSE
      }

      if (response.errors) {
        return {
          data: null,
          errors: response.errors,
        }
      }

      return response
    })
    .catch((error) => {
      // Check if the axios request got cancelled.
      if (axios.isCancel(error)) {
        throw error
      }

      // Check for organization suspended error (403 with specific message)
      const status = error.response?.status
      const errorMessage = error.response?.data?.error
      const brandTheme = error.response?.data?.brand_theme

      if (status === 403 && errorMessage === SUSPENDED_ORG_ERROR_MESSAGE) {
        throw new OrganizationSuspendedError(brandTheme)
      }

      throw error
    })
}

const globalHeaders: Record<string, string> = {
  "Content-Type": "application/json",
};

export function updateAuthToken(token: string | null) {
  globalHeaders["Authorization"] = `Bearer ${token}`;
}

export function getGlobalHeaders(): Record<string, string> {
  return { ...globalHeaders };
}

export function createRelayEnvironment(accessToken: string | null) {
  updateAuthToken(accessToken);

  return new Environment({
    network: Network.create((operation, variables, cacheConfig, uploadables) => 
      fetchQuery(operation, variables, cacheConfig, uploadables, globalHeaders)
    ),
    store: new Store(new RecordSource(), { queryCacheExpirationTime: cacheTTL }),
  });
}

export { fetchQuery }